import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class LegacyLeafletScriptLoaderService {
    private loaded = false;
    private loadPromise?: Promise<void>;
    private readonly skipPathPrefixes = ['/landing', '/auth', '/setup', '/screensaver'];

    private readonly scriptUrls: string[] = [
        'assets/vendor/leaflet/dist/leaflet-src.js',
        'assets/vendor/leaflet.fullscreen/Control.FullScreen.js',
        'assets/vendor/leaflet-sidebar-v2/js/leaflet-sidebar.min.js',
        'assets/vendor/leaflet-responsive-popup/leaflet.responsive.popup.js',
        'assets/vendor/leaflet-draw/dist/leaflet.draw-src.js',
        'assets/vendor/geoman/dist/leaflet-geoman.min.js',
        'assets/vendor/leaflet-control-geocoder/dist/Control.Geocoder.min.js',
        'assets/vendor/easy-pie-chart/dist/easypiechart.min.js',
        'assets/vendor/tilelayer-canvas/index.min.js',
        'assets/js/leaflet.swoopyarrow.min.js',
        'assets/js/Leaflet.Control.Custom.js',
        'assets/js/leaflet.style-editor.min.js',
        'assets/js/Leaflet.Marker.SlideTo.js'
    ];

    isLoaded(): boolean {
        return this.loaded;
    }

    shouldLoadForPath(urlPath: string): boolean {
        const path = (urlPath || '').split('?')[0].split('#')[0].toLowerCase();
        if (!path || path === '/') {
            return false;
        }

        return !this.skipPathPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
    }

    ensureLoaded(): Promise<void> {
        if (this.loaded) {
            return Promise.resolve();
        }

        if (typeof document === 'undefined') {
            this.loaded = true;
            return Promise.resolve();
        }

        if (this.loadPromise) {
            return this.loadPromise;
        }

        this.loadPromise = this.loadSequentially(this.scriptUrls)
            .then(() => {
                this.loaded = true;
            })
            .catch((error) => {
                this.loadPromise = undefined;
                throw error;
            });

        return this.loadPromise;
    }

    private async loadSequentially(urls: string[]): Promise<void> {
        for (const url of urls) {
            await this.loadScript(url);
        }
    }

    private loadScript(url: string): Promise<void> {
        if (this.hasScript(url)) {
            return Promise.resolve();
        }

        return new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.async = false;
            script.defer = false;
            script.dataset.legacyLeafletScript = url;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Failed to load legacy leaflet script: ${url}`));
            document.body.appendChild(script);
        });
    }

    private hasScript(url: string): boolean {
        if (document.querySelector(`script[data-legacy-leaflet-script="${url}"]`)) {
            return true;
        }

        return Array.from(document.scripts).some((script) => {
            const src = script.getAttribute('src') || '';
            return src === url || src.endsWith(url);
        });
    }
}
